import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { usePassport } from 'hooks/usePassport';
import { usePostHogFlag } from 'hooks/usePostHogFlag';
import { WISE_ACCOUNT_QUERY_KEY, useWiseAccount } from 'services/wiseAccount';

export const WISE_CLAIM_REWARD_POPUP_DELAY_MS = 5_000;

const PASSPORT_COUNTRY_PATH = /^\/passport\/[a-z]{2}\/?$/i;
const ELIGIBLE_EXACT_PATHS = new Set(['/passport', '/app/passport/book-mortgage-call', '/fx']);

export const isEligibleWiseClaimRewardPathname = (pathname) => {
  const normalizedPathname = pathname.length > 1 ? pathname.replace(/\/$/, '') : pathname;
  return ELIGIBLE_EXACT_PATHS.has(normalizedPathname) || PASSPORT_COUNTRY_PATH.test(pathname);
};

const Overlay = styled.aside`
  position: fixed;
  top: 50%;
  left: 50%;
  z-index: 998;
  width: min(420px, calc(100vw - 32px));
  max-width: calc(100vw - 32px);
  transform: translate(-50%, -50%);
  overflow: hidden;
  border: 1px solid var(--grey-30);
  border-radius: 20px;
  background: var(--white);
  box-shadow: 0 12px 32px rgba(5, 10, 37, 0.2);

  @media (max-width: 874px) {
    top: auto;
    right: 16px;
    bottom: calc(96px + env(safe-area-inset-bottom));
    left: 16px;
    width: auto;
    max-width: none;
    transform: none;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  color: var(--blue-100);
  background: transparent;
  font: inherit;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid var(--blue-100);
    outline-offset: 2px;
  }
`;

const Content = styled.div`
  display: grid;
  grid-template-columns: 56px minmax(0, 1fr);
  align-items: center;
  gap: 18px;
  padding: 26px 56px 26px 24px;
`;

const Gift = styled.img`
  display: block;
  width: 56px;
  height: 56px;
`;

const Copy = styled.div`
  min-width: 0;
`;

const Title = styled.p`
  margin: 0 0 10px;
  color: var(--blue-100);
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.25;
`;

const ClaimLink = styled(Link)`
  color: #23c599;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 16px;
  font-weight: 600;

  &:visited,
  &:hover,
  &:active,
  &:focus-visible {
    color: #23c599;
  }

  &:focus-visible {
    outline: 2px solid #23c599;
    outline-offset: 2px;
  }
`;

const hasSubmission = (claim) =>
  [claim?.wiseTag, claim?.submissionId, claim?.status].some(
    (value) => value !== null && value !== undefined && String(value).trim() !== ''
  );

function WiseClaimRewardPopup() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const { passportRequests, isPassportRequestsLoading, mortgageCountry } = usePassport();
  const popupFlag = usePostHogFlag('In-app-Wise-claim-reward-popup', {
    ready: !isPassportRequestsLoading && !!mortgageCountry
  });
  const { data: sharedClaim, refetch } = useWiseAccount({ enabled: false });
  const [timerElapsedPath, setTimerElapsedPath] = useState(null);
  const [dismissedPath, setDismissedPath] = useState(null);
  const [authorizedClaim, setAuthorizedClaim] = useState(null);
  const currentPathRef = useRef(pathname);
  currentPathRef.current = pathname;

  const isEligiblePath = isEligibleWiseClaimRewardPathname(pathname);
  const hasCompletedPassport = useMemo(
    () => passportRequests?.some((request) => !!request?.questionnaire_completed) || false,
    [passportRequests]
  );
  const canCheckWiseClaim =
    isEligiblePath &&
    !isPassportRequestsLoading &&
    !!mortgageCountry &&
    hasCompletedPassport &&
    popupFlag === true;

  useEffect(() => {
    setDismissedPath(null);
  }, [pathname]);

  useEffect(() => {
    setTimerElapsedPath(null);

    if (!isEligiblePath || isPassportRequestsLoading || !hasCompletedPassport) {
      return undefined;
    }

    const timeoutId = window.setTimeout(
      () => setTimerElapsedPath(pathname),
      WISE_CLAIM_REWARD_POPUP_DELAY_MS
    );
    return () => window.clearTimeout(timeoutId);
  }, [hasCompletedPassport, isEligiblePath, isPassportRequestsLoading, pathname]);

  useEffect(() => {
    let isCurrentRequest = true;
    const requestPathname = pathname;
    setAuthorizedClaim(null);

    const checkCurrentPath = async () => {
      await queryClient.cancelQueries({ queryKey: WISE_ACCOUNT_QUERY_KEY, exact: true });
      if (!isCurrentRequest || currentPathRef.current !== requestPathname || !canCheckWiseClaim) {
        return;
      }

      const result = await refetch({ cancelRefetch: true });
      if (isCurrentRequest && currentPathRef.current === requestPathname && result.isSuccess) {
        setAuthorizedClaim({ pathname: requestPathname, claim: result.data });
      }
    };

    if (canCheckWiseClaim) {
      checkCurrentPath();
    }

    return () => {
      isCurrentRequest = false;
    };
  }, [canCheckWiseClaim, pathname, queryClient, refetch]);

  const shouldShow =
    canCheckWiseClaim &&
    timerElapsedPath === pathname &&
    dismissedPath !== pathname &&
    popupFlag === true &&
    authorizedClaim?.pathname === pathname &&
    !hasSubmission(authorizedClaim.claim) &&
    !hasSubmission(sharedClaim);

  if (!shouldShow) return null;

  return (
    <Overlay
      role="region"
      aria-live="polite"
      aria-label={t('wiseClaimRewardPopup.regionAccessibilityLabel')}
      data-qa="wise-claim-reward-popup"
    >
      <CloseButton
        type="button"
        aria-label={t('wiseClaimRewardPopup.closeAccessibilityLabel')}
        onClick={() => setDismissedPath(pathname)}
      >
        <span aria-hidden="true">×</span>
      </CloseButton>
      <Content>
        <Gift src="/images/original/icons/gift-solid-full.svg" alt="" />
        <Copy>
          <Title>{t('wiseClaimRewardPopup.title')}</Title>
          <ClaimLink to="/verify-euro-account">{t('wiseClaimRewardPopup.cta')}</ClaimLink>
        </Copy>
      </Content>
    </Overlay>
  );
}

export default WiseClaimRewardPopup;
